import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from "@/lib/supabase"
import { projectKeys } from "@/hooks/queries/use-projects"

export interface Task {
    id: string
    project_id: string
    name: string
    description?: string
    estimated_hours?: number
    status: 'active' | 'completed' | 'archived'
    created_at: string
}

export const taskKeys = {
    all: ['tasks'] as const,
    list: (projectId: string | undefined) => [...taskKeys.all, 'list', projectId] as const,
    notes: (taskId: string | undefined) => [...taskKeys.all, 'notes', taskId] as const,
}

export function useTasks(projectId?: string) {
    const queryClient = useQueryClient()

    const { data: tasks = [], isLoading, refetch } = useQuery({
        queryKey: taskKeys.list(projectId),
        queryFn: async () => {
            if (!projectId) return []
            const { data, error } = await supabase
                .from('tasks')
                .select('*')
                .eq('project_id', projectId)
                .order('created_at', { ascending: false })

            if (error) throw error
            return data as Task[]
        },
        enabled: !!projectId,
    })

    const { mutateAsync: upsertTaskNote } = useMutation({
        mutationFn: async ({ taskId, content, noteId }: { taskId: string, content: string, noteId?: string }) => {
            if (noteId) {
                const { data, error } = await supabase.from('task_notes').update({ content }).eq('id', noteId).select().single()
                if (error) throw error
                return data
            } else {
                const { data, error } = await supabase.from('task_notes').insert({ task_id: taskId, content }).select().single()
                if (error) throw error
                return data
            }
        }
        // No invalidamos notes aquí directamente a menos que sea necesario, o podríamos
    })

    const createTask = async (task: Partial<Task> & { initial_note?: string }) => {
        const { initial_note, ...taskData } = task
        const { data, error } = await supabase
            .from('tasks')
            .insert(taskData)
            .select()
            .single()

        if (error) {
            console.error("Error creating task:", error)
            return null
        }

        if (initial_note?.trim()) {
            await upsertTaskNote({ taskId: data.id, content: initial_note.trim() })
        }

        queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) })
        // Invalidate project stats as well
        queryClient.invalidateQueries({ queryKey: projectKeys.all })
        return data as Task
    }

    const updateTaskStatus = async (taskId: string, status: Task['status']) => {
        const { error } = await supabase
            .from('tasks')
            .update({ status })
            .eq('id', taskId)

        if (error) {
            console.error("Error updating task status:", error)
            return false
        }

        queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) })
        queryClient.invalidateQueries({ queryKey: projectKeys.all })
        return true
    }

    const updateTask = async (taskId: string, updates: Partial<Task> & { initial_note?: string }) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { initial_note, ...taskData } = updates
        const { data, error } = await supabase
            .from('tasks')
            .update(taskData)
            .eq('id', taskId)
            .select()
            .single()

        if (error) {
            console.error("Error updating task:", error)
            return null
        }

        queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) })
        queryClient.invalidateQueries({ queryKey: projectKeys.all })
        return data as Task
    }

    const deleteTask = async (taskId: string) => {
        const { error } = await supabase
            .from('tasks')
            .delete()
            .eq('id', taskId)

        if (error) {
            console.error("Error deleting task:", error)
            return false
        }

        queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) })
        queryClient.invalidateQueries({ queryKey: projectKeys.all })
        return true
    }

    // Keep these as manual functions for now if they are not used as reactive data
    const getTaskNotes = async (taskId: string) => {
        const { data, error } = await supabase
            .from('task_notes')
            .select('*')
            .eq('task_id', taskId)
            .order('created_at', { ascending: false })

        if (error) {
            console.error("Error fetching task notes:", error)
            return []
        }
        return data || []
    }

    const deleteTaskNote = async (noteId: string) => {
        const { error } = await supabase.from('task_notes').delete().eq('id', noteId)
        if (error) {
            console.error("Error deleting task note:", error)
            return false
        }
        return true
    }

    return {
        tasks,
        isLoading,
        createTask,
        updateTaskStatus,
        updateTask,
        deleteTask,
        refreshTasks: async () => { await refetch() },
        getTaskNotes,
        upsertTaskNote: async (taskId: string, content: string, noteId?: string) => await upsertTaskNote({ taskId, content, noteId }),
        deleteTaskNote
    }
}
